import React, { useEffect, useMemo, useState } from 'react'
import '../styles/AdminDashboard.css'
import '../styles/AdminGalleryPage.css'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const ADMIN_TOKEN_KEY = 'einv_admin_token'
const ADMIN_GALLERY_ALBUM_KEY = 'einv_admin_gallery_album'

function getApi(path) {
  return `${API_BASE_URL}${path}`
}

function formatDate(value) {
  if (!value) return 'Unknown date'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown date'
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatBytes(value) {
  const bytes = Number(value) || 0
  if (bytes <= 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function AuthorizedPreview({ path, token, alt, onUnauthorized }) {
  const [objectUrl, setObjectUrl] = useState('')
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    let nextObjectUrl = ''
    setFailed(false)
    setObjectUrl('')

    if (!path || !token) {
      setFailed(true)
      return undefined
    }

    fetch(getApi(path), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        if (response.status === 401) {
          onUnauthorized()
          throw new Error('Admin session expired.')
        }
        if (!response.ok) throw new Error('Preview unavailable.')
        return response.blob()
      })
      .then((blob) => {
        if (cancelled) return
        nextObjectUrl = URL.createObjectURL(blob)
        setObjectUrl(nextObjectUrl)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl)
    }
  }, [path, token, onUnauthorized])

  if (failed) {
    return <div className="adm-gallery-preview-fallback">Photo preview unavailable</div>
  }

  if (!objectUrl) {
    return <div className="adm-gallery-preview-loading">Loading preview…</div>
  }

  return <img src={objectUrl} alt={alt} className="adm-gallery-photo-image" draggable="false" />
}

export default function AdminGalleryPage({ onNavigateTab }) {
  const [token] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || '')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [albums, setAlbums] = useState([])
  const [selectedAlbumId, setSelectedAlbumId] = useState(() => localStorage.getItem(ADMIN_GALLERY_ALBUM_KEY) || '')
  const [photos, setPhotos] = useState([])
  const [albumsLoading, setAlbumsLoading] = useState(false)
  const [photosLoading, setPhotosLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [actionKey, setActionKey] = useState('')
  const [deleteTarget, setDeleteTarget] = useState(null)

  const selectedAlbum = useMemo(
    () => albums.find((album) => album.id === selectedAlbumId) || null,
    [albums, selectedAlbumId]
  )

  const counts = useMemo(() => ({
    total: photos.length,
    public: photos.filter((photo) => photo.privacy === 'public').length,
    private: photos.filter((photo) => photo.privacy === 'private').length,
  }), [photos])

  function handleUnauthorized() {
    localStorage.removeItem(ADMIN_TOKEN_KEY)
    onNavigateTab('invitations')
  }

  async function readResponse(response, fallback) {
    const payload = await response.json().catch(() => ({}))
    if (response.status === 401) {
      handleUnauthorized()
      return null
    }
    if (!response.ok) throw new Error(payload.message || fallback)
    return payload
  }

  async function loadAlbums() {
    if (!token) return
    setAlbumsLoading(true)
    setError('')

    try {
      const response = await fetch(getApi('/admin/invitations/gallery/albums'), {
        headers: { Authorization: `Bearer ${token}` },
      })
      const payload = await readResponse(response, 'Failed to load E-Photo albums.')
      if (!payload) return

      const nextAlbums = Array.isArray(payload.albums) ? payload.albums : []
      setAlbums(nextAlbums)
      setSelectedAlbumId((current) => {
        const retained = nextAlbums.some((album) => album.id === current)
          ? current
          : nextAlbums[0]?.id || ''
        if (retained) localStorage.setItem(ADMIN_GALLERY_ALBUM_KEY, retained)
        else localStorage.removeItem(ADMIN_GALLERY_ALBUM_KEY)
        return retained
      })
    } catch (err) {
      setError(err.message || 'Failed to load E-Photo albums.')
    } finally {
      setAlbumsLoading(false)
    }
  }

  async function loadPhotos(albumId = selectedAlbumId) {
    if (!token || !albumId) {
      setPhotos([])
      return
    }

    setPhotosLoading(true)
    setError('')

    try {
      const response = await fetch(
        getApi(`/admin/invitations/gallery/${encodeURIComponent(albumId)}/photos`),
        { headers: { Authorization: `Bearer ${token}` } }
      )
      const payload = await readResponse(response, 'Failed to load gallery photos.')
      if (!payload) return
      setPhotos(Array.isArray(payload.photos) ? payload.photos : [])
    } catch (err) {
      setError(err.message || 'Failed to load gallery photos.')
      setPhotos([])
    } finally {
      setPhotosLoading(false)
    }
  }

  useEffect(() => {
    if (!token) {
      onNavigateTab('invitations')
      return
    }
    loadAlbums()
  }, [token]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedAlbumId) loadPhotos(selectedAlbumId)
    else setPhotos([])
  }, [selectedAlbumId]) // eslint-disable-line react-hooks/exhaustive-deps

  function chooseAlbum(event) {
    const albumId = event.target.value
    setSelectedAlbumId(albumId)
    setSuccess('')
    if (albumId) localStorage.setItem(ADMIN_GALLERY_ALBUM_KEY, albumId)
  }

  function adjustAlbumCounts(albumId, previousPrivacy, nextPrivacy, deleted = false) {
    setAlbums((current) => current.map((album) => {
      if (album.id !== albumId) return album

      if (deleted) {
        return {
          ...album,
          photoCount: Math.max(0, Number(album.photoCount || 0) - 1),
          publicCount: Math.max(0, Number(album.publicCount || 0) - (previousPrivacy === 'public' ? 1 : 0)),
          privateCount: Math.max(0, Number(album.privateCount || 0) - (previousPrivacy === 'private' ? 1 : 0)),
        }
      }

      return {
        ...album,
        publicCount: Math.max(0, Number(album.publicCount || 0) + (nextPrivacy === 'public' ? 1 : 0) - (previousPrivacy === 'public' ? 1 : 0)),
        privateCount: Math.max(0, Number(album.privateCount || 0) + (nextPrivacy === 'private' ? 1 : 0) - (previousPrivacy === 'private' ? 1 : 0)),
      }
    }))
  }

  async function updatePrivacy(photo, privacy) {
    if (!selectedAlbumId || photo.privacy === privacy || actionKey) return
    const key = `privacy:${photo.submissionId}:${photo.id}`
    setActionKey(key)
    setError('')
    setSuccess('')

    try {
      const response = await fetch(getApi(
        `/admin/invitations/gallery/${encodeURIComponent(selectedAlbumId)}/photos/${encodeURIComponent(photo.submissionId)}/${encodeURIComponent(photo.id)}/privacy`
      ), {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ privacy }),
      })
      const payload = await readResponse(response, 'Failed to update photo privacy.')
      if (!payload) return

      setPhotos((current) => current.map((item) => (
        item.submissionId === photo.submissionId && item.id === photo.id
          ? { ...item, privacy: payload.privacy }
          : item
      )))
      adjustAlbumCounts(selectedAlbumId, photo.privacy, payload.privacy)
      setSuccess(payload.message || 'Photo privacy updated.')
    } catch (err) {
      setError(err.message || 'Failed to update photo privacy.')
    } finally {
      setActionKey('')
    }
  }

  async function confirmDelete() {
    const photo = deleteTarget
    if (!photo || !selectedAlbumId || actionKey) return
    const key = `delete:${photo.submissionId}:${photo.id}`
    setActionKey(key)
    setError('')
    setSuccess('')

    try {
      const response = await fetch(getApi(
        `/admin/invitations/gallery/${encodeURIComponent(selectedAlbumId)}/photos/${encodeURIComponent(photo.submissionId)}/${encodeURIComponent(photo.id)}`
      ), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      const payload = await readResponse(response, 'Failed to delete photo.')
      if (!payload) return

      setPhotos((current) => current.filter((item) => !(
        item.submissionId === photo.submissionId && item.id === photo.id
      )))
      adjustAlbumCounts(selectedAlbumId, photo.privacy, photo.privacy, true)
      setDeleteTarget(null)
      setSuccess(payload.message || 'Photo deleted permanently.')
    } catch (err) {
      setError(err.message || 'Failed to delete photo.')
    } finally {
      setActionKey('')
    }
  }

  async function logout() {
    try {
      await fetch(getApi('/admin/logout'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
    } catch {
      // Local logout still proceeds when the network is unavailable.
    }
    localStorage.removeItem(ADMIN_TOKEN_KEY)
    onNavigateTab('invitations')
  }

  async function refreshAll() {
    setSuccess('')
    await loadAlbums()
    if (selectedAlbumId) await loadPhotos(selectedAlbumId)
  }

  if (!token) {
    return <div className="adm-root adm-gallery-redirect">Returning to admin sign in…</div>
  }

  return (
    <div className="adm-root adm-shell adm-gallery-admin-shell">
      {sidebarOpen && <div className="adm-drawer-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`adm-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div className="adm-sidebar-header">
          <div className="adm-sidebar-brand">
            <div className="adm-sidebar-brand-icon">✉</div>
            <div>
              <div className="adm-sidebar-brand-name">E-Invitation</div>
              <div className="adm-sidebar-brand-sub">Admin Console</div>
            </div>
          </div>
        </div>

        <div className="adm-sidebar-nav">
          <div className="adm-sidebar-label">Dashboard</div>
          <button type="button" className="adm-sidebar-btn" onClick={() => onNavigateTab('invitations')}>
            <span className="adm-sidebar-icon">▦</span> Invitations
          </button>
          <button type="button" className="adm-sidebar-btn" onClick={() => onNavigateTab('bookings')}>
            <span className="adm-sidebar-icon">📋</span> Bookings
          </button>
          <button type="button" className="adm-sidebar-btn" onClick={() => onNavigateTab('reviews')}>
            <span className="adm-sidebar-icon">⭐</span> Reviews
          </button>
          <button type="button" className="adm-sidebar-btn active">
            <span className="adm-sidebar-icon">📷</span> Gallery
            {counts.total > 0 && <span className="adm-sidebar-badge">{counts.total}</span>}
          </button>

          <div className="adm-sidebar-label" style={{ marginTop: '8px' }}>Actions</div>
          <button type="button" className="adm-sidebar-btn" onClick={refreshAll} disabled={albumsLoading || photosLoading}>
            <span className="adm-sidebar-icon">↻</span> Refresh Gallery
          </button>
        </div>

        <div className="adm-sidebar-footer">
          <button type="button" className="adm-sidebar-btn" onClick={logout}>
            <span className="adm-sidebar-icon">⏏</span> Log out
          </button>
        </div>
      </aside>

      <div className="adm-main">
        <div className="adm-topbar">
          <button type="button" className="adm-hamburger" onClick={() => setSidebarOpen((open) => !open)} aria-label="Toggle menu">☰</button>
          <span className="adm-topbar-title">E-Invitation Admin</span>
        </div>

        <main className="adm-page adm-gallery-page">
          <div className="adm-page-header">
            <div>
              <p className="adm-page-eyebrow">E-Photo Admin</p>
              <h1 className="adm-page-title">Gallery Management</h1>
              <p className="adm-page-description">Select an E-Photo invitation, review every uploaded photo, adjust privacy, or permanently delete unwanted images.</p>
            </div>
            <div className="adm-page-actions">
              <button type="button" className="adm-btn adm-btn-secondary adm-btn-sm" onClick={refreshAll} disabled={albumsLoading || photosLoading}>
                ↻ Refresh
              </button>
            </div>
          </div>

          <section className="adm-gallery-selector-card">
            <label className="adm-label" htmlFor="adm-gallery-album-select">Invitation / E-Photo Album</label>
            <select
              id="adm-gallery-album-select"
              className="adm-input adm-gallery-album-select"
              value={selectedAlbumId}
              onChange={chooseAlbum}
              disabled={albumsLoading || albums.length === 0}
            >
              {albums.length === 0 && <option value="">No E-Photo albums configured</option>}
              {albums.map((album) => (
                <option key={album.id} value={album.id}>
                  {album.title} — {album.photoCount} photo{album.photoCount === 1 ? '' : 's'}
                </option>
              ))}
            </select>
            {selectedAlbum && (
              <p className="adm-gallery-album-id">
                Album ID: <strong>{selectedAlbum.id}</strong>
                {selectedAlbum.aliases?.length > 0 && <> · Invitation: <strong>{selectedAlbum.aliases[0]}</strong></>}
              </p>
            )}
          </section>

          {error && <div className="adm-alert adm-alert-error adm-gallery-alert">{error}</div>}
          {success && <div className="adm-alert adm-alert-success adm-gallery-alert">{success}</div>}

          {selectedAlbum && (
            <div className="adm-stats-grid adm-gallery-stats">
              <div className="adm-stat-card" style={{ borderTopColor: '#7c2d12' }}><div className="adm-stat-label">All Photos</div><div className="adm-stat-value" style={{ color: '#7c2d12' }}>{counts.total}</div></div>
              <div className="adm-stat-card" style={{ borderTopColor: '#0f766e' }}><div className="adm-stat-label">Public</div><div className="adm-stat-value" style={{ color: '#0f766e' }}>{counts.public}</div></div>
              <div className="adm-stat-card" style={{ borderTopColor: '#9333ea' }}><div className="adm-stat-label">Private</div><div className="adm-stat-value" style={{ color: '#9333ea' }}>{counts.private}</div></div>
            </div>
          )}

          {albumsLoading && albums.length === 0 ? (
            <div className="adm-loading">Loading E-Photo albums…</div>
          ) : albums.length === 0 ? (
            <div className="adm-gallery-empty">
              <div className="adm-gallery-empty-icon">📷</div>
              <h2>No E-Photo Albums Configured</h2>
              <p>Configured photo albums will appear here automatically.</p>
            </div>
          ) : photosLoading ? (
            <div className="adm-loading">Loading gallery photos…</div>
          ) : photos.length === 0 ? (
            <div className="adm-gallery-empty">
              <div className="adm-gallery-empty-icon">🖼</div>
              <h2>No Photos Yet</h2>
              <p>No guest photos have been uploaded to this E-Photo album.</p>
            </div>
          ) : (
            <section className="adm-gallery-grid" aria-label="Admin gallery photos">
              {photos.map((photo, index) => {
                const privacyKey = `privacy:${photo.submissionId}:${photo.id}`
                const deleteKey = `delete:${photo.submissionId}:${photo.id}`
                const busy = actionKey === privacyKey || actionKey === deleteKey

                return (
                  <article className="adm-gallery-photo-card" key={`${photo.submissionId}:${photo.id}`}>
                    <div className="adm-gallery-photo-preview">
                      <AuthorizedPreview
                        path={photo.previewPath}
                        token={token}
                        alt={`Photo ${index + 1} uploaded by ${photo.guestName}`}
                        onUnauthorized={handleUnauthorized}
                      />
                      <span className={`adm-gallery-privacy-badge adm-gallery-privacy-${photo.privacy}`}>
                        {photo.privacy === 'private' ? 'Private' : 'Public'}
                      </span>
                    </div>

                    <div className="adm-gallery-photo-info">
                      <p className="adm-gallery-uploader-label">Uploaded by</p>
                      <h3 title={photo.guestName}>{photo.guestName}</h3>
                      <p className="adm-gallery-photo-meta">{formatDate(photo.uploadedAt)}</p>
                      <p className="adm-gallery-photo-meta">WebP: {formatBytes(photo.previewSize)}</p>
                    </div>

                    <div className="adm-gallery-photo-actions">
                      <button
                        type="button"
                        className={`adm-btn adm-btn-sm ${photo.privacy === 'public' ? 'adm-btn-primary' : 'adm-btn-secondary'}`}
                        onClick={() => updatePrivacy(photo, 'public')}
                        disabled={busy || photo.privacy === 'public'}
                      >
                        Public
                      </button>
                      <button
                        type="button"
                        className={`adm-btn adm-btn-sm ${photo.privacy === 'private' ? 'adm-btn-primary' : 'adm-btn-secondary'}`}
                        onClick={() => updatePrivacy(photo, 'private')}
                        disabled={busy || photo.privacy === 'private'}
                      >
                        Private
                      </button>
                      <button
                        type="button"
                        className="adm-btn adm-btn-danger adm-btn-sm adm-gallery-delete-button"
                        onClick={() => setDeleteTarget(photo)}
                        disabled={busy}
                      >
                        Delete
                      </button>
                    </div>
                  </article>
                )
              })}
            </section>
          )}
        </main>
      </div>

      {deleteTarget && (
        <div className="adm-gallery-modal-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget && !actionKey) setDeleteTarget(null)
        }}>
          <section className="adm-gallery-delete-modal" role="dialog" aria-modal="true" aria-labelledby="adm-gallery-delete-title">
            <p className="adm-page-eyebrow">Permanent action</p>
            <h2 id="adm-gallery-delete-title">Delete this photo?</h2>
            <p>
              This will permanently remove the WebP preview, JPG download, and photo record uploaded by <strong>{deleteTarget.guestName}</strong>.
            </p>
            <div className="adm-gallery-modal-actions">
              <button type="button" className="adm-btn adm-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={Boolean(actionKey)}>Cancel</button>
              <button type="button" className="adm-btn adm-btn-danger" onClick={confirmDelete} disabled={Boolean(actionKey)}>
                {actionKey.startsWith('delete:') ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
